/**
 * @file: StatusPill.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A small colored status pill (label + tone). Used by lead/reservation lists
 * and detail headers.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronRight } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export type StatusTone = 'neutral' | 'info' | 'active' | 'success' | 'danger';

const ToneColors: Record<StatusTone, { bg: string; fg: string }> = {
  neutral: { bg: '#f1f5f9', fg: '#475569' },
  info: { bg: '#e0f2fe', fg: '#0369a1' },
  // "In progress" — the brand blue, distinct from the cyan `info` / green
  // `success` / red `danger`. Not amber: yellow reads as a warning, but working
  // a lead is the healthy, expected state.
  active: { bg: '#EAF3FF', fg: '#0066FF' },
  success: { bg: '#dcfce7', fg: '#15803d' },
  danger: { bg: '#fee2e2', fg: '#b91c1c' },
};

export const StatusPill = memo(
  ({
    label,
    tone,
    onPress,
  }: {
    label: string;
    tone: StatusTone;
    // When set, the pill is tappable and shows a trailing chevron.
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const colors = ToneColors[tone];
    const style = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      backgroundColor: colors.bg,
      borderRadius: rpx(999),
      paddingHorizontal: rpx(9),
      paddingVertical: rpx(5),
    };
    const content = (
      <>
        <Typography.Text size={rpx(12)} color={colors.fg} bold>
          {label}
        </Typography.Text>
        {onPress ? (
          <ChevronRight
            size={rpx(13)}
            color={colors.fg}
            style={{ marginLeft: rpx(1), marginRight: rpx(-2) }}
          />
        ) : null}
      </>
    );
    return onPress ? (
      <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={style}>
        {content}
      </TouchableOpacity>
    ) : (
      <View style={style}>{content}</View>
    );
  },
);
